import React from 'react';

/**
 * LoadGauge — a half-donut showing ACWR with a color band.
 *
 * Reads `load` from /api/load/{athlete_id}:
 *   { acwr, flag, message, active_days_7, active_days_28 }
 *
 * Flag → color mapping:
 *   under   blue       low load, room to do more
 *   ok      green      sweet spot
 *   watch   amber      starting to climb
 *   spike   red        back off
 */
export default function LoadGauge({ load, size = 200, theme = 'dark' }) {
  if (!load) return null;
  const flag = load.flag || 'no_data';
  const acwr = load.acwr;
  const color = COLORS[flag];

  // Map ACWR (0..2) onto a 180-degree arc.
  const cap = 2;
  const ratio = Math.max(0, Math.min(1, (acwr ?? 1) / cap));
  const angle = ratio * 180; // 0 left, 180 right

  const cx = size / 2;
  const cy = size * 0.7;
  const r = size * 0.42;

  return (
    <div className="flex items-center gap-4">
      <svg width={size} height={size * 0.8} viewBox={`0 0 ${size} ${size * 0.8}`}>
        <defs>
          <linearGradient id="lg-bg" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#60a5fa" />
            <stop offset="35%" stopColor="#00C9A7" />
            <stop offset="65%" stopColor="#fbbf24" />
            <stop offset="100%" stopColor="#ef4444" />
          </linearGradient>
        </defs>
        {/* Half-arc track */}
        <path
          d={arcPath(cx, cy, r, 180, 360)}
          fill="none"
          stroke="url(#lg-bg)"
          strokeWidth="14"
          strokeLinecap="round"
          opacity="0.4"
        />
        {/* Filled segment up to current value */}
        <path
          d={arcPath(cx, cy, r, 180, 180 + angle)}
          fill="none"
          stroke={color}
          strokeWidth="14"
          strokeLinecap="round"
        />
        {/* Needle */}
        <line
          x1={cx} y1={cy}
          x2={cx + Math.cos((Math.PI * (180 + angle)) / 180) * (r - 18)}
          y2={cy + Math.sin((Math.PI * (180 + angle)) / 180) * (r - 18)}
          stroke={color}
          strokeWidth="3"
          strokeLinecap="round"
        />
        <circle cx={cx} cy={cy} r="6" fill={color} />
        <text
          x={cx}
          y={cy - r / 2 - 8}
          textAnchor="middle"
          fontSize="32"
          fontWeight="800"
          fill={theme === 'dark' ? '#f8fafc' : '#0f172a'}
        >
          {acwr ?? '—'}
        </text>
        <text
          x={cx}
          y={cy - r / 2 + 12}
          textAnchor="middle"
          fontSize="11"
          fontWeight="600"
          fill="#94a3b8"
          letterSpacing="2"
        >
          ACWR
        </text>
      </svg>
      <div className="flex-1 text-sm">
        <div
          className="text-[10px] uppercase tracking-widest font-bold inline-block px-2 py-1 rounded mb-2"
          style={{ background: `${color}22`, color, border: `1px solid ${color}55` }}
        >
          {flag === 'no_data' ? 'no data' : flag}
        </div>
        <div className="opacity-90">{load.message}</div>
        <div className="text-xs opacity-60 mt-2">
          {load.active_days_7} of 7 days active this week
        </div>
      </div>
    </div>
  );
}

const COLORS = {
  no_data: '#94a3b8',
  under:   '#60a5fa',
  ok:      '#00C9A7',
  watch:   '#fbbf24',
  spike:   '#ef4444',
};

function arcPath(cx, cy, r, startDeg, endDeg) {
  const s = (Math.PI * startDeg) / 180;
  const e = (Math.PI * endDeg) / 180;
  const x1 = cx + Math.cos(s) * r;
  const y1 = cy + Math.sin(s) * r;
  const x2 = cx + Math.cos(e) * r;
  const y2 = cy + Math.sin(e) * r;
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
}
